import axios from 'axios'
import { quoteLineType } from '../../types'
import { ImpresCatalogItem, ImpresSupplierOffer } from './impresTypes'
import { impresItemToLineItem } from './impresLineItemMapping'

export const QUOTE_LINE_ITEMS_CHANGED_EVENT = 'quoteLineItemsChanged'

export type ImpresCartLineItem = {
  mfgPN: string
  item: ImpresCatalogItem
  supplierName: ImpresSupplierOffer['supplierName']
  qty: number
}

export type AddedLineItem = { lineItemId: string; lineNumber: number }

export type AddCartItemsResult = {
  added: AddedLineItem[]
  failed: string[]
}

export async function addCartItemsToQuote(
  quoteId: string,
  cartItems: ImpresCartLineItem[],
): Promise<AddCartItemsResult> {
  const quoteRes = await axios.get(`/quote/${quoteId}`)
  let nextLineNumber = quoteRes.data.lineItems.filter((li: quoteLineType) => li.LineNumber !== -1).length + 1
  const failed: string[] = []
  const added: AddedLineItem[] = []

  const lineItems = cartItems.map(cartItem => {
    const lineItem = {
      ...impresItemToLineItem(nextLineNumber, cartItem.mfgPN, cartItem.item, cartItem.supplierName, cartItem.qty),
      QuoteId: quoteId,
    }
    added.push({ lineItemId: lineItem.LineItemId as string, lineNumber: nextLineNumber })
    nextLineNumber += 1
    return lineItem
  })

  try {
    await axios.post(`/quote/${quoteId}/impresCatalogLineItems`, { lineItems })
  } catch (err) {
    console.error('Failed to add cart items to quote', err)
    added.length = 0
    failed.push(...cartItems.map(cartItem => `${cartItem.mfgPN} (${cartItem.supplierName})`))
  }

  if (added.length > 0) {
    window.dispatchEvent(new CustomEvent(QUOTE_LINE_ITEMS_CHANGED_EVENT, { detail: { quoteId } }))
  }

  return { added, failed }
}

export async function undoAddedCartItems(quoteId: string, addedLineItems: AddedLineItem[]): Promise<AddedLineItem[]> {
  const remaining: AddedLineItem[] = []
  const byLineNumberDesc = [...addedLineItems].sort((a, b) => b.lineNumber - a.lineNumber)

  for (const { lineItemId, lineNumber } of byLineNumberDesc) {
    try {
      await axios.delete(`/quote/${quoteId}/lineItem/${lineItemId}`, { data: { lineNumber, bundleId: null } })
    } catch (err) {
      console.error(`Failed to undo line item ${lineItemId}`, err)
      remaining.push({ lineItemId, lineNumber })
    }
  }

  if (remaining.length < addedLineItems.length) {
    window.dispatchEvent(new CustomEvent(QUOTE_LINE_ITEMS_CHANGED_EVENT, { detail: { quoteId } }))
  }

  return remaining
}

export function buildSuccessMessage(addedCount: number, totalCount: number): string {
  if (addedCount === totalCount) return 'All items added to quote.'
  if (addedCount === 1) return '1 item added to quote.'
  return `${addedCount} items added to quote.`
}
